import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchDevices, fetchFacets, exportUrl, type Facets } from '../api/devices';
import { useAuth } from '../context/AuthContext';
import { AddDeviceModal } from '../components/AddDeviceModal';
import type { DeviceRow, Pagination, Env } from '../types';
import { KpiCard } from '../components/KpiCard';
import { DeviceMap, type MapPoint, isValidLatLng } from '../components/DeviceMap';
import { DuplicateBadge } from '../components/DuplicateBadge';
import { ProtocolBadge } from '../components/ProtocolBadge';
import { LifecycleBadge, LIFECYCLE_ORDER, LIFECYCLE_META } from '../components/LifecycleBadge';

const TABS: Env[] = ['prod', 'staging'];
const PAGE_SIZE_OPTIONS = [5, 10, 20, 100, 100000]; // 100000 = "Tous"
const EMPTY_FACETS: Facets = { types: [], firmwares: [], clients: [] };

type Dir = 'asc' | 'desc';

/** True when the device carries coordinates that are present but invalid (0,0 / out of range). */
function hasBadCoords(d: DeviceRow): boolean {
  return d.latitude != null && d.longitude != null && !isValidLatLng(d.latitude, d.longitude);
}

export function InventoryPage() {
  const [tab, setTab] = useState<Env>('prod');
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'operator' || user?.role === 'admin';
  const [showAdd, setShowAdd] = useState(false);

  const [rows, setRows] = useState<DeviceRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [facets, setFacets] = useState<Facets>(EMPTY_FACETS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // filters: search is a debounced text input; the rest are immediate selects.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [fw, setFw] = useState('');
  const [lifecycle, setLifecycle] = useState('');
  const [client, setClient] = useState('');
  // sort + paging
  const [sort, setSort] = useState('');
  const [dir, setDir] = useState<Dir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Switching environment: refresh facets and clear filters (values differ per env).
  useEffect(() => {
    setType('');
    setStatus('');
    setFw('');
    setLifecycle('');
    setClient('');
    setSearch('');
    fetchFacets(tab)
      .then((r) => setFacets(r.data))
      .catch(() => setFacets(EMPTY_FACETS));
  }, [tab]);

  useEffect(() => {
    setPage(1);
  }, [tab, debouncedSearch, type, status, fw, lifecycle, client, sort, dir, pageSize]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('env', tab);
    if (debouncedSearch) p.set('search', debouncedSearch);
    if (type) p.set('type', type);
    if (status) p.set('status', status);
    if (fw) p.set('fw', fw);
    if (lifecycle) p.set('lifecycle', lifecycle);
    if (client) p.set('client', client);
    if (sort) {
      p.set('sort', sort);
      p.set('dir', dir);
    }
    p.set('page', String(page));
    p.set('pageSize', String(pageSize));
    return p;
  }, [tab, debouncedSearch, type, status, fw, lifecycle, client, sort, dir, page, pageSize]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchDevices(params)
      .then((r) => {
        setRows(r.data);
        setPagination(r.pagination ?? null);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [params]);

  function toggleSort(field: string) {
    if (sort === field) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(field);
      setDir('asc');
    }
  }

  const mapPoints: MapPoint[] = rows
    .filter((d) => d.deviceSn && isValidLatLng(d.latitude, d.longitude))
    .map((d) => ({
      sn: d.deviceSn as string,
      name: d.deviceName || d.deviceSn,
      lat: d.latitude as number,
      lng: d.longitude as number,
    }));

  const onlineCount = rows.filter((d) => d.online === 1).length;
  const total = pagination?.total ?? rows.length;

  const exportParams = new URLSearchParams(params);
  exportParams.delete('page');
  exportParams.delete('pageSize');

  // Column order: Nom, Type, N. serie, then the rest, then "Infos" (non-sortable) last.
  const columns: Array<{ key: string; label: string; sortable?: boolean }> = [
    { key: 'deviceName', label: 'Nom' },
    { key: 'deviceType', label: 'Type' },
    { key: 'protocol', label: 'Protocole' },
    { key: 'deviceSn', label: 'N. serie' },
    { key: 'zceSerialNumber', label: 'S/N ZCE' },
    { key: 'hardwareVersion', label: 'HW' },
    { key: 'firmwareVersion', label: 'FW' },
    { key: 'clientName', label: 'Nom de client' },
    { key: 'ownerName', label: 'Client' },
    { key: 'placeName', label: 'Station' },
    { key: 'devicePrice', label: 'Prix' },
    { key: 'deviceStatus', label: 'Statut' },
    { key: 'lifecycleStatus', label: 'Cycle de vie' },
    { key: 'infos', label: 'Infos', sortable: false },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="page-title">Inventaire de la flotte</h1>
        <div className="flex gap-2">
          <a href={exportUrl(exportParams)} className="btn-outline">
            Export CSV
          </a>
          {canEdit && (
            <button type="button" onClick={() => setShowAdd(true)} className="btn">
              + Ajouter une borne
            </button>
          )}
        </div>
      </div>

      {showAdd && (
        <AddDeviceModal
          env={tab}
          onClose={() => setShowAdd(false)}
          onCreated={(sn, env) => {
            setShowAdd(false);
            navigate(`/devices/${encodeURIComponent(sn)}?env=${env}`);
          }}
        />
      )}

      {/* Environment tabs */}
      <div className="tabs">
        {TABS.map((e) => {
          const active = tab === e;
          return (
            <button
              key={e}
              type="button"
              onClick={() => setTab(e)}
              className={`tab inline-flex items-center gap-1.5 ${active ? 'tab-active' : ''}`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${e === 'prod' ? 'bg-rose-400' : 'bg-cyan-400'}`}
              />
              {e}
            </button>
          );
        })}
      </div>

      <div className="stat-strip sm:grid-cols-4">
        <KpiCard label="Appareils (filtre)" value={total} hint={`environnement ${tab}`} />
        <KpiCard label="En ligne (page)" value={onlineCount} hint="connectees (MQTT ou OCPP)" />
        <KpiCard label="Types" value={facets.types.length} />
        <KpiCard label="Page" value={`${pagination?.page ?? 1}/${pagination?.totalPages ?? 1}`} />
      </div>

      <div className="filterbar">
        <div className="flex-1 min-w-[200px]">
          <label className="label">Recherche</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="N. de serie, S/N ZCE, nom, email"
            className="input"
          />
        </div>
        <div>
          <label className="label">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="input w-auto min-w-[140px]"
          >
            <option value="">Tous</option>
            {facets.types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">FW</label>
          <select
            value={fw}
            onChange={(e) => setFw(e.target.value)}
            className="input w-auto max-w-[150px]"
          >
            <option value="">Tous</option>
            {facets.firmwares.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Client</label>
          <select
            value={client}
            onChange={(e) => setClient(e.target.value)}
            className="input w-auto max-w-[180px]"
          >
            <option value="">Tous</option>
            {facets.clients.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Statut</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="input w-auto min-w-[140px]"
          >
            <option value="">Tous</option>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="maintenance">maintenance</option>
            <option value="unavailable">unavailable</option>
          </select>
        </div>
        <div>
          <label className="label">Cycle de vie</label>
          <select
            value={lifecycle}
            onChange={(e) => setLifecycle(e.target.value)}
            className="input w-auto min-w-[140px]"
          >
            <option value="">Tous</option>
            {LIFECYCLE_ORDER.map((s) => (
              <option key={s} value={s}>
                {LIFECYCLE_META[s].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Par page</label>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="input w-auto"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n === 100000 ? 'Tous' : n}
              </option>
            ))}
          </select>
        </div>
        <button type="button" onClick={() => setShowMap((v) => !v)} className="btn-outline">
          {showMap ? 'Masquer la carte' : 'Afficher la carte'}
        </button>
      </div>

      {showMap && (
        <div className="panel p-0 map-frame">
          <DeviceMap points={mapPoints} />
        </div>
      )}

      <div className="panel">
        <div className="table-wrap">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {columns.map((c) => {
                  if (c.sortable === false) {
                    return (
                      <th key={c.key} className="th">
                        {c.label}
                      </th>
                    );
                  }
                  const activeSort = sort === c.key;
                  return (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c.key)}
                      className={`th cursor-pointer hover:text-[var(--t-strong)] ${activeSort ? '!text-[var(--link)]' : ''}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {activeSort ? (
                          <span className="t-link">{dir === 'asc' ? '▲' : '▼'}</span>
                        ) : (
                          <span className="t-faint">↕</span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-[13px] t-muted"
                  >
                    Chargement...
                  </td>
                </tr>
              )}
              {error && !loading && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-[13px] text-red-400"
                  >
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length}>
                    <div className="empty-state">
                      <div className="text-sm font-medium t-body">Aucun appareil.</div>
                      <div className="text-[13px] t-muted">
                        Aucune borne ne correspond a ces filtres.
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                rows.map((d) => {
                  // Un appareil MQTT sans fiche `devices` n'a pas de page detail.
                  const orphan = d.source === 'mqtt';
                  // La fiche s'ouvre pour les deux : borne OCPP et appareil MQTT.
                  const to = d.deviceSn
                    ? `/devices/${encodeURIComponent(d.deviceSn)}?env=${d.env}`
                    : null;
                  return (
                    <tr key={`${d.env}-${d.deviceId}`} className="tr-hover">
                      <td className="td">
                        {to ? (
                          <Link to={to} className="font-medium t-link hover:underline">
                            {d.deviceName || d.deviceSn}
                          </Link>
                        ) : (
                          // Sans fiche, le nom est celui donne dans le dashboard, sinon le S/N.
                          <span className="font-medium t-body">
                            {d.dashboardName || d.deviceName || d.deviceSn || '-'}
                          </span>
                        )}
                        {orphan && (
                          <span
                            className="block text-[11px] text-sky-300/80"
                            title="Appareil MQTT : connu de mqtt_device_states et du dashboard, sans ligne devices"
                          >
                            appareil MQTT
                          </span>
                        )}
                      </td>
                      <td className="td">{d.deviceType || '-'}</td>
                      <td className="td">
                        <ProtocolBadge protocol={d.protocol} />
                      </td>
                      <td className="td">
                        {to ? (
                          <Link to={to} className="font-medium t-link hover:underline">
                            {d.deviceSn}
                          </Link>
                        ) : (
                          d.deviceSn || '-'
                        )}
                      </td>
                      <td className="td">
                        {d.zceSerialNumber || <span className="t-faint">-</span>}
                      </td>
                      <td className="td">{d.hardwareVersion || '-'}</td>
                      <td className="td">
                        {d.firmwareVersion || '-'}
                        {d.firmwareLive && (
                          <span
                            className="ml-1 text-[10px] t-faint uppercase"
                            title="Version annoncee par l'appareil a mqtt-service (cache du service, pas en base)"
                          >
                            live
                          </span>
                        )}
                      </td>
                      <td className="td">{d.clientName || <span className="t-faint">-</span>}</td>
                      <td className="td">
                        {d.ownerUuid ? (
                          <Link
                            to={`/clients/${encodeURIComponent(d.ownerUuid)}?env=${d.env}`}
                            className="t-link hover:underline"
                          >
                            {d.ownerName || d.ownerEmail || 'Client'}
                          </Link>
                        ) : d.orgName ? (
                          // Pas de proprietaire dans l'app : l'organisation qui l'a revendique dans le dashboard.
                          <span
                            title={`Organisation du dashboard${d.orgType ? ` (${d.orgType})` : ''}`}
                          >
                            <span className="t-body">{d.orgName}</span>
                            {(d.orgAddedBy || d.orgAddedAt) && (
                              <span className="block text-[11px] t-faint">
                                ajoute{d.orgAddedBy ? ` par ${d.orgAddedBy}` : ''}
                                {d.orgAddedAt ? ` le ${d.orgAddedAt.slice(0, 10)}` : ''}
                              </span>
                            )}
                          </span>
                        ) : (
                          d.ownerName || d.ownerEmail || '-'
                        )}
                      </td>
                      <td className="td">
                        {d.placeName ||
                          d.placeAddress ||
                          (d.installationName ? (
                            // Wattel EM : pas de station, mais une installation > zone dans le dashboard.
                            <span title="Installation et zone du dashboard">
                              {d.installationName}
                              {d.zoneName ? ` › ${d.zoneName}` : ''}
                            </span>
                          ) : (
                            '-'
                          ))}
                      </td>
                      <td className="td num text-right">
                        {d.devicePrice != null ? `${d.devicePrice.toFixed(2)} EUR` : '-'}
                      </td>
                      <td className="td">
                        <StatusBadge
                          status={d.deviceStatus}
                          online={d.online}
                          source={d.source}
                          lastSeen={d.mqttLastSeen}
                        />
                      </td>
                      <td className="td">
                        <LifecycleBadge status={d.lifecycleStatus} />
                      </td>
                      <td className="td">
                        {(() => {
                          const badCoords = hasBadCoords(d);
                          const dupCount = d.duplicates?.length ?? 0;
                          if (!badCoords && dupCount === 0)
                            return <span className="t-faint">-</span>;
                          return (
                            <span className="inline-flex items-center gap-2">
                              {badCoords && (
                                <span
                                  title="Coordonnees GPS invalides (0,0) : appareil non localisable sur la carte"
                                  className="cursor-help text-amber-500 text-base"
                                  aria-label="Coordonnees GPS invalides (0,0)"
                                >
                                  &#9888;
                                </span>
                              )}
                              <DuplicateBadge device={d} />
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {pagination && pagination.totalPages > 1 && (
          <div className="pagination">
            <span className="num">{pagination.total} appareils</span>
            <div className="flex gap-2 items-center">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="btn-ghost"
              >
                Precedent
              </button>
              <span className="num px-2 py-1">
                {pagination.page}/{pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="btn-ghost"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({
  status,
  online,
  source,
  lastSeen,
}: {
  readonly status: string | null;
  readonly online: number | null;
  readonly source?: 'devices' | 'mqtt';
  readonly lastSeen?: string | null;
}) {
  // Appareil MQTT sans fiche : pas de device_status, seul l'etat de connexion existe.
  if (source === 'mqtt' && !status) {
    const on = online === 1;
    return (
      <span
        className={on ? 'pill pill-green' : 'pill pill-gray'}
        title={
          lastSeen
            ? `Dernier contact MQTT : ${lastSeen.slice(0, 16)}`
            : 'Jamais vu par mqtt-service'
        }
      >
        <span className="pill-dot" />
        {on ? 'en ligne' : 'hors ligne'}
      </span>
    );
  }
  const color =
    status === 'active'
      ? 'pill pill-green'
      : status === 'maintenance'
        ? 'pill pill-amber'
        : 'pill pill-gray';
  return (
    <span className="inline-flex items-center gap-1">
      <span className={color}>
        <span className="pill-dot" />
        {status || '-'}
      </span>
      {online === 1 && (
        <span
          className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]"
          title="En ligne (MQTT ou OCPP)"
        />
      )}
    </span>
  );
}
